/*
 * Copyright (c) 2026 Bluemate B.V.
 *
 * PWA lifecycle banners — render-only component that owns the state and
 * effects for:
 *   1. Silent service-worker auto-update: a short "app updated" toast after
 *      the automatic reload, and a failure banner shown ONLY when the auto
 *      update stalls (self-dismisses if a later background attempt succeeds).
 *   2. Offline indicator.
 *   3. Install banners. One of seven mutually-exclusive variants shows
 *      depending on platform / browser:
 *
 *      MOBILE (4 banners)
 *      ┌─────────────────────────────────────────────────────────────────┐
 *      │ iOS Non-Safari (Chrome iOS / Firefox iOS / Edge iOS / Opera iOS):
 *      │   → text + "Open in Safari" button (x-safari-https:// scheme)
 *      │ iOS Safari:
 *      │   → text + 3-step share-icon → Add to Home Screen instructions
 *      │ Android compatible (any Chromium that fired beforeinstallprompt):
 *      │   → text + native "Install" button
 *      │ Android incompatible (Samsung Internet, Firefox Android, others):
 *      │   → text + "Open in Chrome" button → Android Intent URL targeting
 *      │     Chrome (package=com.android.chrome) with Play Store fallback.
 *      │     Bare intent URL without a package= constraint is unreliable
 *      │     on Samsung Internet — Chrome receives the intent without the
 *      │     data URI and opens at its search page.
 *      └─────────────────────────────────────────────────────────────────┘
 *
 *      DESKTOP (3 banners)
 *      ┌─────────────────────────────────────────────────────────────────┐
 *      │ Mac Safari (macOS Sonoma+): → Add to Dock instructions
 *      │ Firefox Desktop: → text + "open in Chrome" guidance
 *      │ Unsupported browser: → warning, no install path available
 *      └─────────────────────────────────────────────────────────────────┘
 *
 * Dismissal is session-scoped (sessionStorage, not localStorage): a closed
 * hint stays hidden across reloads/remounts within the session, but a fresh
 * session re-evaluates visibility, so the nudge can return (e.g. after an
 * uninstall). EXCEPTION: the two iOS hints show whenever we are on iOS in a
 * browser (hidden only once running as the installed PWA). They can be hidden
 * temporarily via InfoBanner's own close button, but that dismiss is in-memory
 * only (no featureKey → not persisted), so it returns on reload. iOS Safari
 * gives a browser tab no reliable "installed" signal, so any PERSISTED
 * installed-state guess there left the hint stuck hidden after an install —
 * an in-memory-only dismiss is the KISS fix that can never get stuck.
 */

import React, {useEffect, useState} from 'react';

import {useTranslation} from 'react-i18next';

import InfoBanner from '../misc/infoBanner';

import AndroidInstallPromptBanner from './androidInstallPromptBanner';

import {
    dismissManualInstallHint,
    hasInstallPrompt,
    isAndroid,
    isFirefox,
    isInstallHintSnoozed,
    isIOS,
    isIOSNonSafari,
    isMacSafari,
    isManualInstallHintDismissed,
    isPWAInstalled,
    isPWASupported,
    isSamsungBrowser,
    isStandalone,
    openInChrome,
    promptInstall,
    PWA_INSTALL_AVAILABLE_EVENT,
    PWA_JUST_UPDATED_KEY,
    snoozeInstallHint,
    useOnlineStatus
} from '../../util/pwaUtil';

import {onShowConfirmSnackbar} from '../../util/renderUtil';

import {useBreakpoint} from '../../util/useBreakpoint';

import {MOBILE_APP_MENU_INSTALL_ROUTE, MOBILE_APP_ROUTE} from '../../util/constants';

import {isPwaInstalledOnThisDevice} from '../../util/pwaInstallState';

import {useLocation} from 'react-router';

export default function PWABanners() {
    const {t} = useTranslation();
    const {isMobileLayout} = useBreakpoint();
    const online = useOnlineStatus();
    const {pathname} = useLocation();
    // The Mobile App page IS the dedicated install guide — an "install me"
    // banner there is redundant, so suppress the install nudges on that route.
    const onMobileAppPage = pathname === MOBILE_APP_ROUTE;
    // Initial state computed from isStandalone — avoids a render flicker
    // where banners appear briefly on standalone launch before useEffect
    // gets to hide them. Re-evaluated on `appinstalled` below.
    const [chromiumInstallVisible, setChromiumInstallVisible] = useState(false);
    // Seed from BOTH the install state and a session-scoped dismissal flag, so a
    // hint the user already closed this session does not flash back on reload /
    // new tab / remount (reviewer findings #1/#5). The flag lives in
    // sessionStorage, so a fresh session (or post-uninstall) shows it again.
    const [manualHintVisible, setManualHintVisible] = useState(
        () => !isPWAInstalled() && !isManualInstallHintDismissed()
    );
    // In-flight spinner for the Install CTA — set on tap, cleared on error (a
    // successful install hides the banner). Blocks re-taps while in flight.
    const [installLoading, setInstallLoading] = useState(false);
    // False while a browser/system surface has taken focus — notably Chrome's own
    // install sheet, which renders over the page and would otherwise sit on top of
    // a banner claiming the browser offers no install.
    const [windowFocused, setWindowFocused] = useState(() =>
        typeof document === 'undefined' ? true : document.hasFocus()
    );
    // Tri-state, `undefined` until the platform answers. A browser TAB cannot see
    // standalone mode, and on Chromium Android the local "installed" flag is
    // deliberately ignored (getInstalledRelatedApps is authoritative there), so
    // `isPWAInstalled()` reads false even for an installed app. The manual-install
    // hint below claims the browser offers no install — shown to someone who
    // already has the app, that is simply wrong, so it waits for the real answer.
    const [installedHere, setInstalledHere] = useState<boolean | undefined>(undefined);
    // Closing the install guide hint quiets it for a day rather than for good:
    // it is the only way in when the browser offers no install button.
    const [hintSnoozed, setHintSnoozed] = useState(() => isInstallHintSnoozed());

    useEffect(() => {
        let cancelled = false;
        const check = () =>
            void isPwaInstalledOnThisDevice().then(value => {
                if (!cancelled) setInstalledHere(value);
            });
        check();
        // `beforeinstallprompt` means "installable, so not installed"; `appinstalled`
        // is the other edge. Re-ask on both.
        window.addEventListener(PWA_INSTALL_AVAILABLE_EVENT, check);
        window.addEventListener('appinstalled', check);
        return () => {
            cancelled = true;
            window.removeEventListener(PWA_INSTALL_AVAILABLE_EVENT, check);
            window.removeEventListener('appinstalled', check);
        };
    }, []);

    useEffect(() => {
        const onFocus = () => setWindowFocused(true);
        const onBlur = () => setWindowFocused(false);
        window.addEventListener('focus', onFocus);
        window.addEventListener('blur', onBlur);
        return () => {
            window.removeEventListener('focus', onFocus);
            window.removeEventListener('blur', onBlur);
        };
    }, []);

    // Post-reload "app updated" toast: after a silent auto-update reload, show a
    // short confirmation once (no action required), then clear the flag.
    useEffect(() => {
        try {
            if (sessionStorage.getItem(PWA_JUST_UPDATED_KEY) === '1') {
                sessionStorage.removeItem(PWA_JUST_UPDATED_KEY);
                onShowConfirmSnackbar(t('pwa.updatedToast', 'Bluemate is bijgewerkt naar de nieuwste versie.'));
            }
        } catch {
            /* storage disabled — no toast */
        }
    }, []);

    // Toast shown when an Update / Install action fails.
    function showActionError() {
        onShowConfirmSnackbar(t('pwa.actionError', 'Er ging iets mis. Probeer het opnieuw.'), {
            backgroundColor: 'color-red-warning',
            textColor: 'white'
        });
    }

    async function onClickInstall() {
        if (installLoading) return;
        setInstallLoading(true);
        try {
            const result = await promptInstall();
            // 'unavailable' = beforeinstallprompt never fired (nothing to install);
            // 'accepted'/'dismissed' = the native dialog ran. Only treat the first
            // as an error. On accept, the `appinstalled` handler hides the banner.
            if (result === 'unavailable') showActionError();
            else setChromiumInstallVisible(false);
        } catch {
            showActionError();
        } finally {
            setInstallLoading(false);
        }
    }

    // User-initiated dismissal of a manual install hint (Android / Mac / Firefox
    // / unsupported banners). Persists a session-scoped flag so it stays hidden
    // across reloads/remounts this session, then hides it for the current
    // render; a fresh session shows it again. The iOS hints below deliberately
    // do NOT use this — they show whenever we are on iOS in a browser.
    // Dismissing the install-guide hint: quiet for a day, then it returns.
    function onSnoozeInstallHint() {
        snoozeInstallHint();
        setHintSnoozed(true);
    }

    function dismissManualHint() {
        dismissManualInstallHint();
        setManualHintVisible(false);
    }

    // Single source of truth for "is PWA installed in this browser":
    //   - On mount: isPWAInstalled() === true means the app runs standalone OR a
    //     previous `appinstalled` persisted the flag (so the regular browser tab
    //     knows too) → hide every install banner. This is the fix for the bug
    //     where, after installing, the browser tab still showed the "your
    //     browser can't install" hint: isStandalone() is false in that tab and
    //     an installed app no longer fires beforeinstallprompt, so without the
    //     persisted flag the incompatible-browser banner appeared incorrectly.
    //   - On `appinstalled`: fired in the regular browser tab when the user
    //     accepts an install. isStandalone() is still false here but the install
    //     is done — we hide manual + chromium banners so the user is not pestered.
    //   - On `beforeinstallprompt` (PWA_INSTALL_AVAILABLE_EVENT): the captured
    //     prompt is now available → show chromium banner (the event handler in
    //     pwaUtil also clears the persisted installed flag here).
    //   - After uninstall: browser fires beforeinstallprompt again on the next
    //     eligible visit → flag cleared → banners show again.
    useEffect(() => {
        function evaluate() {
            if (isPWAInstalled()) {
                setChromiumInstallVisible(false);
                setManualHintVisible(false);
                return;
            }
            setChromiumInstallVisible(hasInstallPrompt());
        }
        function onAppInstalled() {
            // `appinstalled` fires in the regular browser tab even though
            // isStandalone() is still false there. Force-hide banners now;
            // they will re-evaluate from isStandalone on next page load.
            setChromiumInstallVisible(false);
            setManualHintVisible(false);
        }
        evaluate();
        window.addEventListener(PWA_INSTALL_AVAILABLE_EVENT, evaluate);
        window.addEventListener('appinstalled', onAppInstalled);
        return () => {
            window.removeEventListener(PWA_INSTALL_AVAILABLE_EVENT, evaluate);
            window.removeEventListener('appinstalled', onAppInstalled);
        };
    }, []);

    /** Action helpers */

    function openCurrentUrlInSafari() {
        // x-safari-https:// scheme triggers Safari to open the URL on iOS
        // Chrome / Firefox / Edge / Opera (documented since iOS 13). Safari
        // itself ignores the scheme — harmless to call, but we gate the
        // button on isIOSNonSafari() so it only appears where it helps.
        const url = window.location.href.replace(/^https?:\/\//, '');
        window.location.href = `x-safari-https://${url}`;
    }

    function onRetryConnection() {
        if (navigator.onLine) {
            onShowConfirmSnackbar(t('pwa.offlineReconnected', 'Verbinding hersteld'));
            window.location.reload();
            return;
        }
        onShowConfirmSnackbar(t('pwa.offlineStillNoConnection', 'Nog steeds geen verbinding'), {
            backgroundColor: 'color-blue-bluemate',
            textColor: 'white'
        });
    }

    /** Non-install banners */

    function renderOfflineBanner() {
        if (online) return null;
        return (
            <InfoBanner
                variant='critical'
                title={t('pwa.offlineTitle', 'Geen verbinding')}
                persistDismiss={false}
                primaryBannerButton
                actionLabel={t('pwa.offlineRetry', 'Opnieuw proberen')}
                onAction={onRetryConnection}
            >
                {t('pwa.offlineMessage', 'Controleer je internetverbinding en probeer het opnieuw.')}
            </InfoBanner>
        );
    }

    /** Mobile install banners */

    /** #1 — iOS Non-Safari (Chrome iOS / Firefox iOS / Edge iOS / Opera iOS).
     *  Apple restricts Add-to-Home-Screen to Safari, so the only useful UX
     *  is a one-tap escape into Safari. Shown on any iOS non-Safari browser
     *  (these can never run as the installed PWA). It can be hidden temporarily
     *  via InfoBanner's built-in close button (persistDismiss with no featureKey
     *  → in-memory only, NOT persisted), so it returns on reload and can never
     *  get permanently stuck hidden after an install. */
    function renderIOSNonSafariHint() {
        if (!isIOSNonSafari()) return null;
        return (
            <InfoBanner
                variant='info'
                title={t('pwa.iosInstallTitle', 'Installeer Bluemate op je iPhone of iPad')}
                primaryBannerButton
                actionLabel={t('pwa.openInSafari', 'Open in Safari')}
                onAction={openCurrentUrlInSafari}
            >
                {t(
                    'pwa.iosNonSafariInstallHint',
                    'Bluemate kan op iOS alleen via Safari op het beginscherm worden geïnstalleerd. Tik op de knop hieronder om deze pagina in Safari te openen en de installatie te starten.'
                )}
            </InfoBanner>
        );
    }

    /** #2 — iOS Safari. Apple does not fire beforeinstallprompt, so we
     *  show the share-icon → Add to Home Screen instructions. Shown on iOS
     *  Safari while in the browser tab (hidden only once running as the
     *  installed PWA, isStandalone()). It can be hidden temporarily via
     *  InfoBanner's built-in close button (persistDismiss with no featureKey →
     *  in-memory only, NOT persisted), so it returns on reload and can never
     *  get permanently stuck hidden after an install. */
    function renderIOSSafariHint() {
        if (!isIOS() || isIOSNonSafari() || isStandalone()) return null;
        return (
            <InfoBanner
                variant='info'
                title={t('pwa.iosInstallTitle', 'Installeer Bluemate op je iPhone of iPad')}
                primaryBannerButton
                actionLabel={t('pwa.iosInstallGuide', 'Installatiegids')}
                actionRoute={MOBILE_APP_ROUTE}
            >
                {t(
                    'pwa.iosInstallHint',
                    'Als geïnstalleerde app start Bluemate sneller en kun je push-meldingen ontvangen over koopblok-wissels, lage voorraad en nieuwe reviews. Bekijk de installatiegids voor de stappen.'
                )}
            </InfoBanner>
        );
    }

    /** #3 — Android compatible (Chromium with beforeinstallprompt fired).
     *  One-tap native install via the captured prompt event. */
    function renderChromiumInstallBanner() {
        if (!chromiumInstallVisible) return null;
        if (!isMobileLayout) return null;
        // When the UA claims a platform with its own dedicated banner (#1/#2/#5/#6),
        // that banner owns the slot. Those browsers never fire beforeinstallprompt
        // themselves; only a Chromium spoofing their UA does (e.g. DevTools device
        // emulation), which would otherwise show two install banners at once.
        if (isIOS() || isMacSafari() || isFirefox()) return null;
        return (
            <AndroidInstallPromptBanner
                variant='install'
                onAction={onClickInstall}
                actionLoading={installLoading}
                onClose={() => setChromiumInstallVisible(false)}
            />
        );
    }

    /** #4 — Android incompatible: any Android browser that did NOT fire
     *  beforeinstallprompt. Covers Samsung Internet, Firefox Android,
     *  and any other Android browser without PWA install support.
     *  Action: open a custom picker dialog listing 5 compatible browsers. */
    function renderAndroidIncompatibleHint() {
        if (!isAndroid() || hasInstallPrompt()) return null;
        if (isIOS()) return null; // belt-and-braces (no Android device is also iOS)
        // Only show the "open in Chrome" hint on browsers that genuinely lack
        // one-click install (Samsung Internet, Firefox Android). Chrome (and
        // other Chromium browsers) DO support install — they just don't fire
        // beforeinstallprompt when the PWA is ALREADY installed or not yet
        // eligible. Showing "your browser can't install, open in Chrome" to a
        // Chrome user who already installed via Chrome is wrong, so exclude them.
        // Chromium withholds `beforeinstallprompt` in more cases than "already
        // installed" — a Cloudflare challenge on the manifest, desktop-site mode,
        // its own promotion throttle. The browser menu still installs in every one
        // of those states, so instead of a dead space where the Install button
        // belongs, point at the guide — the same shape as the iOS banner, which
        // has always worked this way because iOS never fires the event either.
        if (!isSamsungBrowser() && !isFirefox()) {
            // While Chrome's own install sheet is up, the page keeps rendering
            // behind it — and this banner, pointing at the manual guide, sits
            // right under the sheet that installs in one tap. The sheet takes
            // focus, so drop the banner while we do not have it and bring it
            // back when the user returns.
            if (!windowFocused) return null;
            // Only when the platform has positively said "not installed". Anything
            // else — installed, or not answered yet — shows nothing.
            if (installedHere !== false) return null;
            if (hintSnoozed) return null;
            return (
                <InfoBanner
                    variant='info'
                    title={t('pwa.androidManualInstallTitle', 'Installeer Bluemate op je Android-toestel')}
                    primaryBannerButton
                    actionLabel={t('pwa.androidInstallGuide', 'Installatiegids')}
                    actionRoute={MOBILE_APP_MENU_INSTALL_ROUTE}
                    onClose={onSnoozeInstallHint}
                >
                    {t(
                        'pwa.androidManualInstallHint',
                        "Als geïnstalleerde app start Bluemate sneller en kun je push-meldingen ontvangen over koopblok-wissels, lage voorraad en nieuwe reviews. Tik hieronder op 'Installatiegids' voor de stappen."
                    )}
                </InfoBanner>
            );
        }
        if (!manualHintVisible) return null;
        return (
            <AndroidInstallPromptBanner variant='open-in-chrome' onAction={openInChrome} onClose={dismissManualHint} />
        );
    }

    /** Desktop install banners*/
    /** #5 — Mac Safari (macOS Sonoma+ supports Add to Dock). */
    function renderMacSafariHint() {
        // PWA-08: no desktop install nudges — mobile & tablet only.
        if (!manualHintVisible || !isMacSafari() || !isMobileLayout) return null;
        return (
            <InfoBanner
                variant='info'
                title={t('pwa.macSafariInstallTitle', 'Installeer Bluemate op je Mac')}
                persistDismiss={false}
                onClose={dismissManualHint}
            >
                {t(
                    'pwa.macSafariInstallHint',
                    "Als geïnstalleerde app opent Bluemate in een eigen venster zonder adresbalk. Zo installeer je de app in Safari:\n1. Klik in de adresbalk op het deel-icoon (vierkantje met pijl omhoog).\n2. Kies 'Voeg toe aan Dock'.\n3. Bluemate verschijnt in je Dock — start hem daarvandaan zoals elke andere Mac-app. Vereist macOS Sonoma (14) of nieuwer."
                )}
            </InfoBanner>
        );
    }

    /** #6 — Firefox Desktop. Firefox for desktop has no built-in PWA
     *  installer; redirect users to another browser. */
    function renderFirefoxDesktopHint() {
        // PWA-08: no desktop install nudges — mobile & tablet only.
        if (!manualHintVisible || !isMobileLayout) return null;
        if (!isFirefox() || isAndroid()) return null; // Firefox Android handled by #4
        return (
            <InfoBanner
                variant='info'
                title={t('pwa.firefoxDesktopInstallTitle', 'Bluemate installeren in Firefox voor desktop')}
                persistDismiss={false}
                onClose={dismissManualHint}
            >
                {t(
                    'pwa.firefoxDesktopInstallHint',
                    'Bluemate kan als app worden geïnstalleerd — het start sneller en ontvangt push-meldingen. Firefox voor desktop heeft echter geen ingebouwde installatieknop voor web-apps. Je hebt twee opties:\n1. Open deze pagina in Chrome, Edge of Safari om Bluemate met één klik te installeren (aanbevolen).\n2. Of maak een handmatige snelkoppeling naar deze pagina op je bureaublad via je besturingssysteem.'
                )}
            </InfoBanner>
        );
    }

    /** #7 — Unsupported browser (no Service Worker / Notification / Push
     *  API). Warning only — no install path available. */
    function renderUnsupportedHint() {
        if (!manualHintVisible || isPWASupported()) return null;
        // Skip platforms already handled by mobile banners (which DO appear
        // even when isPWASupported() returns true for iOS Safari etc.).
        if (isIOS() || isMacSafari() || isAndroid() || isFirefox()) return null;
        return (
            <InfoBanner
                variant='warning'
                title={t('pwa.unsupportedBrowserTitle', 'Browser wordt niet volledig ondersteund')}
                persistDismiss={false}
                onClose={dismissManualHint}
            >
                {t(
                    'pwa.unsupportedBrowserHint',
                    'Je huidige browser ondersteunt geen moderne web-app functies. Hierdoor werken push-meldingen en de installatie als app niet. Open Bluemate in een recente versie van Chrome, Edge, Safari of Firefox voor de volledige ervaring — al deze browsers zijn gratis beschikbaar.'
                )}
            </InfoBanner>
        );
    }

    return (
        <>
            {renderOfflineBanner()}
            {/* Install nudges are hidden on the Mobile App page (it is the install
                guide itself); the offline/update banners still show there. */}
            {!onMobileAppPage && (
                <>
                    {renderChromiumInstallBanner()}
                    {renderIOSNonSafariHint()}
                    {renderIOSSafariHint()}
                    {renderAndroidIncompatibleHint()}
                    {renderMacSafariHint()}
                    {renderFirefoxDesktopHint()}
                    {renderUnsupportedHint()}
                </>
            )}
        </>
    );
}
